import { motion } from "framer-motion";
import { Activity, Bell, CheckCircle, Smartphone, Layers, Cpu } from "lucide-react";

const features = [
  {
    icon: Activity,
    title: "Live session monitoring",
    description: "See what your agent is doing in real time. Streaming logs, current file, execution state — all visible from your phone.",
    span: "col-span-1 sm:col-span-2 lg:col-span-2",
  },
  {
    icon: Bell,
    title: "Push notifications that matter",
    description: "Get alerted when a run completes, fails, or needs input. No noise — only actionable moments.",
    span: "col-span-1 lg:col-span-1",
  },
  {
    icon: CheckCircle,
    title: "One-tap approvals",
    description: "Approve, deny, stop, or resume without opening a full remote session. Just tap and go.",
    span: "col-span-1 lg:col-span-1",
  },
  {
    icon: Smartphone,
    title: "Mobile control for coding agents",
    description: "Respond to Claude Code, Codex CLI, and terminal workflows from anywhere. Purpose-built for AI coding sessions.",
    span: "col-span-1 sm:col-span-2 lg:col-span-2",
  },
  {
    icon: Layers,
    title: "Persistent session layer",
    description: "Your work keeps running even when you step away. Sessions persist across disconnects and sleep.",
    span: "col-span-1 lg:col-span-1",
  },
  {
    icon: Cpu,
    title: "Built for AI-native developers",
    description: "Designed for the new reality of long-running agent workflows. Not another SSH app.",
    span: "col-span-1 lg:col-span-1",
  },
];

export default function FeaturesSection() {
  return (
    <section
      id="features"
      data-testid="features-section"
      className="py-24 px-6"
      style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
    >
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mb-16"
        >
          <span
            className="text-xs uppercase tracking-wider mb-4 block"
            style={{
              color: "#A1A1AA",
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "11px",
            }}
          >
            Features
          </span>
          <h2
            className="text-3xl sm:text-4xl font-semibold tracking-tight text-white leading-tight"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Everything you need to stay in control.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px" style={{ background: "rgba(255,255,255,0.06)" }}>
          {features.map((feature, i) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className={`p-8 ${feature.span}`}
              style={{ background: "#0A0A0A" }}
            >
              <feature.icon
                className="w-5 h-5 mb-4"
                style={{ color: "#fff" }}
                strokeWidth={1.5}
              />
              <h3
                className="text-base font-medium text-white mb-2"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                {feature.title}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: "#71717A" }}>
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
